import {
  Component,
  DoCheck,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  DestroyRef,
  ViewEncapsulation,
  computed,
  inject,
  signal,
  viewChild,
  CUSTOM_ELEMENTS_SCHEMA
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormControl, ValidatorFn, Validators } from '@angular/forms';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { firstValueFrom } from 'rxjs';

import { Client } from 'src/providers/client/client';
import { Aws } from 'src/providers/aws/aws';

import { CameraProvider } from 'src/shared/providers/camera/camera';
import { randomUUID } from 'src/tidy-ui-components/utils/secure-utils';

import { ChannelDetailComponent } from 'src/pages/marketing/components/channel-detail/channel-detail.component';
import { WebsiteDomainSettingsComponent } from 'src/pages/marketing/components/website-domain-settings/website-domain-settings.component';
import { ListingsSetupComponent } from 'src/shared/components/listings-setup/listings-setup.component';
import { ConfirmPage, ConfirmPageParamsModel } from 'src/pages/confirm/confirm.component';
import { BookingSettingsPage } from 'src/pages/revenue/booking-settings/booking-settings.page';
import { OptimizerSaveBarService, SaveBarOwner } from 'src/shared/services/optimizer-save-bar.service';

import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { WindowService } from 'src/shared/providers/window.service';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import {
  ListingPhoto,
  ListingPhotoRoom,
  AmenityOption,
  UNIFIED_AMENITIES
} from 'src/shared/services/property-listings-mock.service';
import { AddressesApiService } from 'src/shared/services/addresses-api.service';
import { AddressListing } from 'src/shared/services/marketing-channels.service';
import {
  AddressSetting,
  ListingOptimizationSettingsService,
  LISTING_SETTING_CATEGORY,
  PlatformSyncStatus,
  UpsertListingEntry,
} from 'src/shared/services/listing-optimization-settings.service';
import {
  listingContentToEntries,
  ListingContentValues,
  listingContentFromSettings,
  listingRoomsFromSettings,
  listingPhotosToEntry,
  listingSpacesToEntry,
  DEFAULT_LISTING_PHOTO_ROOMS,
  LISTING_PROPERTY_TYPE_ITEMS,
  LISTING_PLACE_TYPES,
  LISTING_CANCELLATION_POLICIES,
  LISTING_SAFETY_ITEMS,
  AMENITY_SAFETY_SLUGS,
  LISTING_BED_TYPES,
  MAX_LISTING_HIGHLIGHTS,
  ListingHighlight,
  PHOTOS_SETTING_KEY,
} from './listing-content.model';
import { aggregateSyncStatus, coveragePlatforms, failedSyncPlatforms, PlatformSyncError, refusedPhotoReasons, syncFailureReasons, pendingSyncPlatforms, syncedSyncPlatforms, unsupportedSyncPlatforms } from 'src/pages/revenue/booking-settings/booking-settings.model';
import { PlatformPillsComponent } from 'src/pages/revenue/booking-settings/platform-pills.component';
import { SyncStatusDotComponent, SyncDotInfo } from 'src/shared/components/sync-status-dot/sync-status-dot.component';
import { DIRECT_BOOKING_PLATFORM, PLATFORM_MARK_LOGOS, formatPlatformName, getConnectingLabel, getListingPlatform } from 'src/shared/constants/listing-platforms';
import { DirectBookingSitesService, WebsiteConfig } from 'src/shared/services/direct-booking-sites.service';
import {
  CHANNEL_STATUS_ITEMS,
  DIRECT_BOOKING_STATUS_ITEMS,
  describeChannelStatus,
  directBookingSelectValue
} from './property-listings.status';

import { IonicModule, ModalController } from '@ionic/angular';
import { Util } from 'src/shared/util/util';
import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

import { DateTime } from 'luxon';

// The direct-booking row shown before the address has a tidy listing. Not a
// backend id: turning it Listed creates the listing (see onStatusSelect).
const DIRECT_BOOKING_PLACEHOLDER_ID = 0;
// The strictest connected-channel photo minimum (VRBO's 1024x683) — uploads below it
// still work but sync as interpolated upscales, so the picker warns at add time.
const MIN_LISTING_PHOTO_WIDTH = 1024;
const MIN_LISTING_PHOTO_HEIGHT = 683;
// Native picks are scaled to fit inside this square: up to a 3:1 photo, both sides
// stay above the minimum.
const LISTING_PHOTO_MAX_SIDE = 2048;

// One connected listing = one platform. `id` is the backend address_listing_id
// (what the PUT targets). Rows read and write `rawStatus` (the backend
// vocabulary) through the status dropdown.
interface HighlightRow {
  title: FormControl<string>;
  body: FormControl<string>;
}

interface ListingRow {
  id: number;
  platform: string;
  platformLabel: string;
  platformId: string;
  rawStatus: string;
  statusDescription: string;
  connectedAt: string;
  logo: string;
  // The tidy row is this property's direct booking site rather than an external
  // channel, so it reads, links and saves differently from every other row.
  isDirectBooking: boolean;
  // "Last optimized: <date>" (or "Not optimized yet") for a real external
  // listing; null for direct-booking and placeholder rows, which TIDY doesn't
  // optimize. Precomputed here so the template stays function-free.
  lastOptimizedLabel: string | null;
}

@Component({
  selector: 'property-listings',
  templateUrl: './property-listings.page.html',
  styleUrls: ['./property-listings.page.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IonicModule,
    DragDropModule,
    TidyUiComponentsModule,
    SharedModule,
    SyncStatusDotComponent,
    PlatformPillsComponent,
    BookingSettingsPage
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class PropertyListingsPage implements OnInit, DoCheck {
  private rightSidePanelService = inject(RightSidePanelService);
  private navCtrl = inject(CustomNavController);
  private storage = inject(TidyStorage);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private fb = inject(FormBuilder);
  private listingSettings = inject(ListingOptimizationSettingsService);
  private addressesApi = inject(AddressesApiService);
  private client = inject(Client);
  private directBookingSites = inject(DirectBookingSitesService);
  private aws = inject(Aws);
  private camera = inject(CameraProvider);
  private modalController = inject(ModalController);
  private util = inject(Util);
  windowService = inject(WindowService);

  readonly optimizerSettings = viewChild(BookingSettingsPage);
  private readonly saveBar = inject(OptimizerSaveBarService);

  // What the fields held when they last loaded or saved; null until the first load lands.
  private savedSnapshot: string | null = null;
  private readonly contentDirty = signal(false);
  private readonly isActive = signal(true);
  private readonly isSavingChanges = signal(false);

  // The same floating "Unsaved changes" bar as the Optimizer, lifted over the right-side
  // panel the builder opens in on desktop. It covers the embedded Optimizer fields too.
  private readonly saveBarOwner: SaveBarOwner = {
    visible: computed(() => this.isActive()
      && (this.contentDirty() || !!this.optimizerSettings()?.isDirty())),
    saving: computed(() => this.isSavingChanges()),
    overPanel: true,
    save: () => { void this.save(); },
    discard: () => { void this.discardChanges(); },
  };

  isLoading = true;
  isSavingForm = false;
  isSavingListing = false;
  isUploadingPhoto = false;
  propertyLabel = '';

  listings: ListingRow[] = [];
  statusItems = CHANNEL_STATUS_ITEMS;
  directBookingStatusItems = DIRECT_BOOKING_STATUS_ITEMS;
  // One control per row so each dropdown holds its own status.
  statusControls: Record<number, FormControl<string>> = {};
  amenities: AmenityOption[] = UNIFIED_AMENITIES;
  amenityState: Record<string, boolean> = {};
  safetyItems: AmenityOption[] = LISTING_SAFETY_ITEMS;
  // Disclosure chips only — the alarm chips read/write amenityState (see isSafetySelected).
  safetyItemState: Record<string, boolean> = {};
  propertyTypeItems = LISTING_PROPERTY_TYPE_ITEMS;
  // Blank first: place type must stay unset until the host picks one (an unpicked
  // dropdown is an absent key, see listingContentToEntries), never default to a value.
  placeTypeItems = [{ value: '', viewValue: 'Not set' }, ...LISTING_PLACE_TYPES];
  cancellationPolicyItems = LISTING_CANCELLATION_POLICIES;
  bedTypes = LISTING_BED_TYPES;
  maxHighlights = MAX_LISTING_HIGHLIGHTS;
  // Whether a save must write the spaces row back: true once a row exists (its doc
  // reconciles authoritatively on the platforms) or the user edited rooms/beds. A
  // default scaffold is never pushed as the property's real structure.
  private hasSpacesRow = false;
  private spacesDirty = false;
  // property_type rides the address record, not a listing row; only a changed pick saves.
  private initialPropertyType = '';
  // What the form loaded with, so a list emptied since then saves as a clear (see
  // listingContentToEntries) rather than an absent key the backend leaves untouched.
  private loadedContent: ListingContentValues | null = null;
  // House rules and highlights are variable-length lists, so each row gets its own
  // control(s) — same pattern as the photo caption controls.
  houseRuleControls: FormControl<string>[] = [];
  highlightControls: HighlightRow[] = [];
  // Flips on the first save attempt with a half-filled highlight so its empty half shows inline.
  highlightsSubmitted = false;
  rooms: ListingPhotoRoom[] = [];
  captionControls: Record<string, FormControl<string>> = {};
  // Why a platform would not take a photo, keyed by that photo's own id, for the tile badge.
  photoRefusals: Record<string, string> = {};
  // Per-field channel-sync status for the listing-content dots, keyed by setting key.
  private syncByKey: Record<string, Partial<Record<string, PlatformSyncStatus>>> = {};
  private syncErrorsByKey: Record<string, Record<string, PlatformSyncError>> = {};

  protected addressId: number;
  private address: any = null;
  private isOpeningDirectBookingSite = false;

  form = this.fb.group({
    title: [''],
    description: [''],
    securityDeposit: [null as number | null, [Validators.min(0)]],
    bedrooms: [null as number | null, [Validators.min(0), Validators.max(50)]],
    bathrooms: [null as number | null, [Validators.min(0), Validators.max(50)]],
    maxGuests: [null as number | null, [Validators.min(0), Validators.max(50)]],
    squareFeet: [null as number | null, [Validators.min(0)]],
    propertyType: [''],
    placeType: [''],
    cancellationPolicy: [''],
    neighborhoodDescription: [''],
  });

  // Total beds is derived from the per-room bed types — a second editable total could
  // only contradict them, and no platform accepts a bare count anyway. It rides a
  // disabled control so it lays out like the editable counts beside it; the control
  // carries the disabled state itself because the form directive resets tidy-input's
  // [disableElement] from it on init.
  bedsControl = this.fb.nonNullable.control({ value: 0, disabled: true });

  // bedrooms/bathrooms reach no platform (SYNC_MATRIX channels: []) — they feed the
  // direct booking page. Once a spaces row exists (or the host edited rooms) that
  // structure is what the platforms show, so Bedrooms becomes the bedroom-room count,
  // read-only, and is still saved so the direct booking page agrees. Bathrooms can't be
  // derived (half baths, no bathroom rooms in spaces) and stays a plain store-only field.
  get roomStructureIsAuthoritative(): boolean {
    return this.hasSpacesRow || this.spacesDirty;
  }

  private refreshRoomCounts(): void {
    const beds = this.rooms.reduce(
      (sum, room) => sum + (room.beds ?? []).reduce((count, bed) => count + bed.count, 0), 0
    );
    this.bedsControl.setValue(beds, { emitEvent: false });
    if (this.roomStructureIsAuthoritative) {
      this.form.controls.bedrooms.setValue(this.bedroomRooms.length, { emitEvent: false });
      this.form.controls.bedrooms.disable({ emitEvent: false });
    }
  }

  get isSaving(): boolean {
    return this.isSavingForm || this.isSavingListing || this.isUploadingPhoto;
  }

  get selectedAmenitiesCount(): number {
    return this.amenities.filter(amenity => this.amenityState[amenity.slug]).length;
  }

  async ngOnInit() {
    this.rightSidePanelService.setDialogPageTitle('Listings');
    this.destroyRef.onDestroy(() => this.rightSidePanelService.headerSpinnerVisible$.next(false));
    const params = await this.storage.retrieve('dialog-params');
    this.addressId = params?.addressId || Number(localStorage.getItem('addressId')) || 0;
    this.saveBar.claim(this.saveBarOwner);
    this.destroyRef.onDestroy(() => this.saveBar.release(this.saveBarOwner));
    this.loadData();
    this.loadPropertyLabel();
  }

  // Edits land through many paths (form controls, chips, photo uploads, drag order,
  // room and highlight rows), so compare against the last saved state on every check
  // rather than hooking each one.
  ngDoCheck(): void {
    const dirty = this.savedSnapshot !== null
      && (this.editSnapshot() !== this.savedSnapshot || this.propertyTypeChanged());
    if (dirty !== this.contentDirty()) { this.contentDirty.set(dirty); }
  }

  ionViewWillEnter(): void {
    this.isActive.set(true);
    this.saveBar.claim(this.saveBarOwner);
    this.optimizerSettings()?.reloadIfClean();
  }

  ionViewWillLeave(): void {
    this.isActive.set(false);
  }

  // Everything the builder's own save writes but property type, which loads on its own
  // after the fields and saves separately, so it is compared against its loaded value.
  private editSnapshot(): string {
    return JSON.stringify({
      content: this.currentContentValues(),
      rooms: this.roomsWithCaptions(),
      cancellationPolicy: this.form.getRawValue().cancellationPolicy ?? '',
      standalone: this.standaloneSettingEntries(),
    });
  }

  private propertyTypeChanged(): boolean {
    const propertyType = this.form.getRawValue().propertyType;
    return !!propertyType && propertyType !== this.initialPropertyType;
  }

  private markSaved(): void {
    this.savedSnapshot = this.editSnapshot();
    this.contentDirty.set(false);
  }

  // Re-reads the property, so every builder field goes back to what is saved.
  private discardChanges = async (): Promise<void> => {
    this.optimizerSettings()?.discardChanges();
    this.form.patchValue({ propertyType: this.initialPropertyType }, { emitEvent: false });
    this.savedSnapshot = null;
    this.spacesDirty = false;
    this.highlightsSubmitted = false;
    await this.loadEditFields();
    this.markSaved();
    this.cdr.markForCheck();
  };

  save = async (): Promise<void> => {
    if (!this.validateHighlights()) {
      return;
    }
    const confirmParams: ConfirmPageParamsModel = {
      title: 'Save Changes?',
      body: 'Are you sure you want to apply these edits to all connected listings for this property?',
      backText: 'Go Back',
      confirmText: 'Save Changes',
      confirmAction: this.confirmSave,
    };
    const confirmationModal = await this.modalController.create({
      component: ConfirmPage,
      componentProps: confirmParams,
      animated: false,
      cssClass: 'confirm-modal',
    });
    confirmationModal.present();
  };

  // Two writes with no shared rollback: the listing rows go first and the address
  // record's property_type only once they land, so a rejected upsert leaves the property
  // type as it was. Each failure says which of the two didn't save.
  confirmSave = async (): Promise<void> => {
    this.modalController.dismiss();
    this.isSavingForm = true;
    this.isSavingChanges.set(true);
    this.updateHeaderSpinner();
    this.cdr.markForCheck();
    try {
      const content = this.currentContentValues();
      try {
        await this.listingSettings.upsertListingContent(this.addressId, this.collectListingContent(content));
      } catch (err) {
        this.util.showError(this.errorMessage(err, 'Couldn’t save your changes. Please try again.'));
        return;
      }
      this.loadedContent = content;
      if (this.spacesDirty) {
        this.hasSpacesRow = true;
        this.spacesDirty = false;
      }
      this.markSaved();
      const propertyTypeSaved = await this.savePropertyType();
      const optimizerSaved = (await this.optimizerSettings()?.saveFromHost()) ?? true;
      await this.refreshSyncStatus();
      if (propertyTypeSaved && optimizerSaved) {
        this.util.showSuccess('Changes saved to all connected listings.');
      }
    } finally {
      this.isSavingForm = false;
      this.isSavingChanges.set(false);
      this.updateHeaderSpinner();
      this.cdr.markForCheck();
    }
  };

  // Only a changed pick writes; false means the write failed (and was already reported).
  private async savePropertyType(): Promise<boolean> {
    const propertyType = this.form.getRawValue().propertyType ?? '';
    if (!propertyType || propertyType === this.initialPropertyType) {
      return true;
    }
    try {
      await firstValueFrom(this.addressesApi.updateAddress(this.addressId, { property_type: propertyType }));
      this.initialPropertyType = propertyType;
      return true;
    } catch {
      this.util.showError('Your listing changes saved, but the property type didn’t. Please try again.');
      return false;
    }
  }

  // The Listing Details fields, all with a real backend home. Price is omitted (no
  // backend home); property type rides the address record (see confirmSave). Photos and
  // spaces ride the same `listing`-category upsert as their own rows.
  private collectListingContent(content: ListingContentValues) {
    const entries = listingContentToEntries(content, this.loadedContent ?? undefined);
    const photos = listingPhotosToEntry(this.roomsWithCaptions());
    if (photos) { entries.push(photos); }
    if (this.hasSpacesRow || this.spacesDirty) {
      entries.push(listingSpacesToEntry(this.rooms));
    }
    // cancellation_policy is an optimizer row shared with Booking Settings; only a
    // picked value is sent (the upsert rejects blanks, and an unpicked dropdown must
    // not overwrite what Booking Settings saved).
    const cancellationPolicy = this.form.getRawValue().cancellationPolicy ?? '';
    if (cancellationPolicy) {
      entries.push({ key: 'cancellation_policy', category: 'optimizer', data: { value: cancellationPolicy } });
    }
    entries.push(...this.standaloneSettingEntries());
    return entries;
  }

  private currentContentValues(): ListingContentValues {
    const value = this.form.getRawValue();
    return {
      title: value.title ?? '',
      description: value.description ?? '',
      securityDepositDollars: this.toNumber(value.securityDeposit),
      bedrooms: this.toNumber(value.bedrooms),
      bathrooms: this.toNumber(value.bathrooms),
      maxGuests: this.toNumber(value.maxGuests),
      squareFeet: this.toNumber(value.squareFeet),
      placeType: value.placeType ?? '',
      neighborhoodDescription: value.neighborhoodDescription ?? '',
      houseRules: this.houseRuleControls.map(control => control.value.trim()).filter(rule => rule !== ''),
      safetyItems: this.safetyItems
        .filter(item => !AMENITY_SAFETY_SLUGS.includes(item.slug) && this.safetyItemState[item.slug])
        .map(item => item.slug),
      highlights: this.collectHighlights(),
      amenities: this.amenityBlobState(),
    };
  }

  // A half-filled highlight fails the whole upsert server-side, so it's caught before the
  // confirm modal with the empty half flagged inline instead of a generic toast after.
  private validateHighlights(): boolean {
    this.highlightControls.forEach(row => {
      row.title.updateValueAndValidity({ emitEvent: false });
      row.body.updateValueAndValidity({ emitEvent: false });
    });
    const valid = this.highlightControls.every(row => row.title.valid && row.body.valid);
    if (!valid) {
      this.highlightsSubmitted = true;
      this.util.showError('Each highlight needs both a title and details.');
      this.cdr.markForCheck();
    }
    return valid;
  }

  // Blank rows are dropped rather than saved as empty highlights; the cap is enforced on
  // save too so a stale extra row can never slip through.
  private collectHighlights(): ListingHighlight[] {
    return this.highlightControls
      .map(row => ({ title: row.title.value.trim(), body: row.body.value.trim() }))
      .filter(row => row.title !== '' || row.body !== '')
      .slice(0, MAX_LISTING_HIGHLIGHTS);
  }

  // Rows backed by a setting of their own (pets) are NOT part of the amenities blob — the
  // backend has no amenity for them, so writing them there reaches nothing.
  private amenityBlobState(): Record<string, boolean> {
    const blob: Record<string, boolean> = { ...this.amenityState };
    this.amenities.filter(a => a.settingKey).forEach(a => delete blob[a.slug]);
    return blob;
  }

  // ...they ride as their own rows instead, carrying their own category. The upsert stamps
  // `listing` only as a default, so an entry naming its category wins.
  private standaloneSettingEntries(): UpsertListingEntry[] {
    return this.amenities
      .filter(amenity => amenity.settingKey)
      .map(amenity => ({
        key: amenity.settingKey,
        category: amenity.settingCategory,
        data: { value: !!this.amenityState[amenity.slug] },
      }));
  }

  // Captions are edited through their own FormControls, not on the photo objects — fold the
  // live control values back onto the photos so a save persists the latest text.
  private roomsWithCaptions(): ListingPhotoRoom[] {
    return this.rooms.map(room => ({
      ...room,
      photos: room.photos.map(photo => ({
        ...photo,
        caption: this.captionControls[photo.id]?.value ?? photo.caption,
      })),
    }));
  }

  private async loadPropertyLabel(): Promise<void> {
    const address = await this.fetchAddress();
    if (!address) {
      return;
    }
    this.propertyLabel = address.address_name
      || (address.address2 ? `${address.address1}, ${address.address2}` : address.address1);
    this.rightSidePanelService.setDialogPageSubtitle(this.propertyLabel, 'assets/svg/location-outline.svg');
    this.cdr.markForCheck();
  }

  // The address record backs both the header subtitle and the direct booking
  // site's config, so it's kept once rather than re-listed per use. Failure is
  // silent here: the subtitle is a nice-to-have, and the direct booking click
  // raises its own error.
  private async fetchAddress(): Promise<any> {
    try {
      const addresses = await this.client.getAddresses();
      this.address = addresses?.find((item: any) => item.id === this.addressId) ?? null;
    } catch {
      // Leave this.address null — callers handle it.
    }
    return this.address;
  }

  goToAddListing = () => {
    // Tell Us About Your Listings covers every property × channel on one page;
    // `returnRoute` brings the user back to this editor when they finish.
    this.rightSidePanelService.setDialogPageSubtitle(null);
    this.rightSidePanelService.setDialogPageTitle('Tell Us About Your Listings');
    this.rightSidePanelService.navigateTo(
      'listings-setup',
      { addressId: this.addressId, returnRoute: 'property-listings' },
      ListingsSetupComponent
    );
  };

  goToListingDetail(listing: ListingRow): void {
    // The tidy row is the same property as its Direct Booking sites row, so both
    // doors have to open the same page — not channel-detail, which describes an
    // external listing this property doesn't have.
    if (listing.isDirectBooking) {
      void this.goToDirectBookingSite();
      return;
    }
    // channel-detail expects the backend AddressListing shape.
    const params = {
      addressId: this.addressId,
      listing: {
        id: listing.id,
        address_id: this.addressId,
        platform: listing.platform,
        platform_id: listing.platformId,
        status: 'connected',
        created_at: listing.connectedAt,
      },
    };
    this.goToChannelDetail(params, 'Listing Details');
  }

  private async goToDirectBookingSite(): Promise<void> {
    // Re-entrancy guard: building the config awaits two reads, which is long
    // enough for a second click to land and open a duplicate panel.
    if (this.isOpeningDirectBookingSite) {
      return;
    }
    this.isOpeningDirectBookingSite = true;
    try {
      const address = this.address ?? await this.fetchAddress();
      if (!address) {
        this.util.showError('Could not open your direct booking site. Please try again.');
        return;
      }
      // Never strand the click — the site and editor URLs come from the address
      // alone, and the detail page reads publish state itself on open.
      const config: WebsiteConfig = await this.directBookingSites.buildConfig(address)
        .catch(() => this.directBookingSites.buildLocalConfig(address));
      const params = { websiteConfig: config };
      this.storage.save('dialog-params', params);
      // No setDialogPageTitle here: WebsiteDomainSettingsComponent sets its own
      // title and address subtitle, exactly as when opened from Direct Booking.
      this.rightSidePanelService.setDialogPageSubtitle(null);
      if (this.windowService.isDesktopRes) {
        // Already in a secondary-layer panel — navigate within it, don't drop to base nav.
        this.rightSidePanelService.navigateTo('website-domain-settings', params, WebsiteDomainSettingsComponent);
      } else {
        this.navCtrl.navigateForward('website-domain-settings', params);
      }
    } finally {
      this.isOpeningDirectBookingSite = false;
    }
  }

  private goToChannelDetail(params: any, title: string): void {
    this.storage.save('dialog-params', params);
    this.rightSidePanelService.setDialogPageTitle(title);
    this.rightSidePanelService.setDialogPageSubtitle(null);
    if (this.windowService.isDesktopRes) {
      // Already in a secondary-layer panel — navigate within it, don't drop to base nav.
      this.rightSidePanelService.navigateTo('revenue/listing-detail', params, ChannelDetailComponent);
    } else {
      this.navCtrl.navigateForward('revenue/listing-detail');
    }
  }

  // A row's status dropdown. Optimistic: show the pick now, write, revert the
  // row AND the control on failure. The platform push is asynchronous — a 200
  // means the status was saved, not that the channel accepted it — so the HTTP
  // result is all there is to react to.
  //
  // The direct-booking row is special-cased: re-listing goes through the same
  // publish path as the site page's toggle (a publish can create a fresh
  // listing, so re-read to pick up the new row/id); pausing is a plain write.
  async onStatusSelect(listing: ListingRow, next: string): Promise<void> {
    const previous = listing.rawStatus;
    const displayed = listing.isDirectBooking ? directBookingSelectValue(previous) : previous;
    if (!next || next === displayed) {
      return;
    }
    this.applyRowStatus(listing.id, next);
    this.isSavingListing = true;
    this.updateHeaderSpinner();
    this.cdr.markForCheck();
    try {
      if (listing.isDirectBooking && next === 'listed') {
        // The placeholder row has no listing to PUT — publishing creates it.
        await this.directBookingSites.setPublished(this.addressId, listing.id || null, true);
        await this.loadListings();
      } else {
        await this.client.updateAddressListing(listing.id, { status: next });
      }
    } catch {
      this.applyRowStatus(listing.id, previous);
      this.statusControls[listing.id]?.setValue(displayed, { emitEvent: false });
      this.util.showError(
        listing.isDirectBooking
          ? 'Could not update direct booking. Please try again.'
          : 'Could not update the listing. Please try again.'
      );
    } finally {
      this.isSavingListing = false;
      this.updateHeaderSpinner();
      this.cdr.markForCheck();
    }
  }

  private applyRowStatus(id: number, rawStatus: string): void {
    this.listings = this.listings.map(row =>
      row.id === id
        ? {
            ...row,
            rawStatus,
            statusDescription: describeChannelStatus(row.isDirectBooking ? directBookingSelectValue(rawStatus) : rawStatus),
          }
        : row
    );
  }

  // Keep one control per row, reusing instances across reloads so an open
  // dropdown isn't torn down; a background status change (e.g. a reconnect
  // adopting the platform's state) still lands via the no-emit setValue.
  private syncStatusControls(): void {
    const next: Record<number, FormControl<string>> = {};
    for (const row of this.listings) {
      if (row.rawStatus === 'connecting') {
        continue;
      }
      const value = row.isDirectBooking ? directBookingSelectValue(row.rawStatus) : row.rawStatus;
      const control = this.statusControls[row.id] ?? this.fb.nonNullable.control(value);
      control.setValue(value, { emitEvent: false });
      next[row.id] = control;
    }
    this.statusControls = next;
  }

  toggleAmenity(slug: string): void {
    this.amenityState = { ...this.amenityState, [slug]: !this.amenityState[slug] };
  }

  // The alarm chips are amenity facts (they push through the amenities blob), so they
  // read/write amenityState; only the disclosure chips carry safetyItemState.
  isSafetySelected(slug: string): boolean {
    return AMENITY_SAFETY_SLUGS.includes(slug) ? !!this.amenityState[slug] : !!this.safetyItemState[slug];
  }

  toggleSafetyItem(slug: string): void {
    if (AMENITY_SAFETY_SLUGS.includes(slug)) {
      this.toggleAmenity(slug);
      return;
    }
    this.safetyItemState = { ...this.safetyItemState, [slug]: !this.safetyItemState[slug] };
  }

  addHouseRule(): void {
    this.houseRuleControls = [...this.houseRuleControls, this.fb.nonNullable.control('')];
  }

  removeHouseRule(index: number): void {
    this.houseRuleControls = this.houseRuleControls.filter((_, i) => i !== index);
  }

  get canAddHighlight(): boolean {
    return this.highlightControls.length < this.maxHighlights;
  }

  addHighlight(): void {
    if (!this.canAddHighlight) {
      return;
    }
    this.highlightControls = [...this.highlightControls, this.newHighlightRow()];
  }

  // A highlight is exactly a title + body (the upsert rejects a half-filled one), so each
  // half is required whenever the other is filled. Both blank is fine: the row is dropped.
  private newHighlightRow(title = '', body = ''): HighlightRow {
    const row = { title: this.fb.nonNullable.control(title), body: this.fb.nonNullable.control(body) };
    const requiredWith = (other: FormControl<string>): ValidatorFn =>
      control => (other.value.trim() !== '' && control.value.trim() === '' ? { required: true } : null);
    row.title.addValidators(requiredWith(row.body));
    row.body.addValidators(requiredWith(row.title));
    return row;
  }

  removeHighlight(index: number): void {
    this.highlightControls = this.highlightControls.filter((_, i) => i !== index);
  }

  // Bed pickers show only on bedroom rooms — the platforms attach bed types to bedroom
  // spaces, so offering them on the kitchen would store something nothing can read.
  isBedroomRoom(room: ListingPhotoRoom): boolean {
    return (room.type ?? '') === 'bedroom' || room.key.startsWith('bedroom');
  }

  // The Sleeping Arrangements card: bedrooms only, room list read-only there
  // (rooms are created and photographed in the Photos card).
  get bedroomRooms(): ListingPhotoRoom[] {
    return this.rooms.filter(room => this.isBedroomRoom(room));
  }

  bedCount(room: ListingPhotoRoom, type: string): number {
    return room.beds?.find(bed => bed.type === type)?.count ?? 0;
  }

  // Steps one bed type's count, rebuilding `beds` in canonical bed-type order and
  // dropping zero-count entries so the stored array only lists beds the room has.
  // Imported bed types outside the stepper vocabulary (crib, bunk, …) are preserved
  // at the end — an edit to the queen count must never silently delete them.
  changeBedCount(room: ListingPhotoRoom, type: string, delta: number): void {
    const counts = new Map<string, number>((room.beds ?? []).map(bed => [bed.type, bed.count]));
    counts.set(type, Math.max(0, (counts.get(type) ?? 0) + delta));
    const known = this.bedTypes.map(bedType => bedType.value);
    const kept = [
      ...known.filter(value => (counts.get(value) ?? 0) > 0),
      ...[...counts.keys()].filter(value => !known.includes(value) && (counts.get(value) ?? 0) > 0),
    ];
    room.beds = kept.map(value => ({ type: value, count: counts.get(value) ?? 0 }));
    this.spacesDirty = true;
    this.rooms = [...this.rooms];
    this.refreshRoomCounts();
  }

  // A new photo-less bedroom the host can set beds on right away. It saves into the
  // spaces row; VRBO's reconcile write creates it on the platform, while Airbnb reports
  // the structural add unsupported until its room create/delete op is captured.
  addRoom(): void {
    let n = this.rooms.filter(room => this.isBedroomRoom(room)).length + 1;
    while (this.rooms.some(room => room.key === `bedroom_${n}`)) { n += 1; }
    this.rooms = [
      ...this.rooms,
      { key: `bedroom_${n}`, name: `Bedroom ${n}`, type: 'bedroom', order: this.rooms.length, photos: [] },
    ];
    this.spacesDirty = true;
    this.refreshRoomCounts();
  }

  onPhotoDrop(room: ListingPhotoRoom, event: CdkDragDrop<ListingPhoto[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    moveItemInArray(room.photos, event.previousIndex, event.currentIndex);
    this.rooms = [...this.rooms];
  }

  // Pick a photo (camera on native, file input on web), upload it straight to S3 via the
  // shared Aws provider — the same client-credential path chat/property-photo uploads use —
  // then append it with its real hosted URL. A cancelled pick is silent.
  async addPhoto(room: ListingPhotoRoom): Promise<void> {
    let picked: { dataUrl: string; format?: string } | undefined;
    try {
      picked = await this.camera.getImg(false, {
        ...this.camera.CAMERA_OPTIONS,
        width: LISTING_PHOTO_MAX_SIDE,
        height: LISTING_PHOTO_MAX_SIDE,
      });
    } catch {
      return;
    }
    if (!picked?.dataUrl) {
      return;
    }
    await this.warnIfBelowListingPhotoMinimum(picked.dataUrl);
    this.isUploadingPhoto = true;
    this.updateHeaderSpinner();
    this.cdr.markForCheck();
    try {
      const id = randomUUID();
      const key = `listing-photos/${this.addressId}/${room.key}/${id}.jpg`;
      const response = await this.aws.uploadFileToS3(picked.dataUrl, key, picked.format || 'image/jpeg');
      // The backend names the file after this uuid when it moves it out of the temp bucket.
      const photo: ListingPhoto = {
        id,
        caption: '',
        order: room.photos.length,
        url: `${response.Location}?uuid=${id}`,
      };
      this.registerCaptionControl(photo);
      room.photos = [...room.photos, photo];
      this.rooms = [...this.rooms];
    } catch {
      this.util.showError('Couldn’t add that photo. Please try again.');
    } finally {
      this.isUploadingPhoto = false;
      this.updateHeaderSpinner();
      this.cdr.markForCheck();
    }
  }

  removePhoto(room: ListingPhotoRoom, photo: ListingPhoto): void {
    room.photos = room.photos.filter(existing => existing.id !== photo.id);
    delete this.captionControls[photo.id];
    this.rooms = [...this.rooms];
  }

  // Booking platforms enforce photo minimums (VRBO: 1024x683); a smaller photo still
  // syncs — the capture service upscales it — but only as interpolated pixels, so it
  // shows blurry there. Warn-and-continue: the upload itself is never blocked, and a
  // photo we cannot measure uploads silently as before.
  private async warnIfBelowListingPhotoMinimum(dataUrl: string): Promise<void> {
    const size = await this.measureImage(dataUrl);
    if (!size || (size.width >= MIN_LISTING_PHOTO_WIDTH && size.height >= MIN_LISTING_PHOTO_HEIGHT)) {
      return;
    }
    await this.util.showWarning(
      `This photo is ${size.width}x${size.height} — below the ${MIN_LISTING_PHOTO_WIDTH}x${MIN_LISTING_PHOTO_HEIGHT} some booking platforms require, so it may look blurry there.`
    );
  }

  private measureImage(dataUrl: string): Promise<{ width: number; height: number } | null> {
    return new Promise(resolve => {
      const image = new Image();
      image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
      image.onerror = () => resolve(null);
      image.src = dataUrl;
    });
  }

  private async loadData(): Promise<void> {
    await this.loadListings();
    await this.loadEditFields();
    this.markSaved();
    this.isLoading = false;
    this.cdr.markForCheck();
  }

  private async loadListings(): Promise<void> {
    try {
      const listings = (await this.client.getAddressListings(this.addressId)) as AddressListing[];
      const rows = (listings || []).map(listing => this.toListingRow(listing));
      this.listings = rows.some(row => row.isDirectBooking)
        ? rows
        : [...rows, this.directBookingPlaceholderRow()];
      this.syncStatusControls();
    } catch {
      this.listings = [];
      this.statusControls = {};
      this.util.showError('Could not load your connected listings.');
    }
  }

  private toListingRow(listing: AddressListing): ListingRow {
    const isDirectBooking = listing.platform === DIRECT_BOOKING_PLATFORM;
    return {
      id: listing.id,
      platform: listing.platform,
      platformLabel: formatPlatformName(listing.platform),
      platformId: listing.platform_id,
      rawStatus: listing.status,
      // A direct-booking row describes its dropdown's collapsed value — legacy
      // `unlisted` reads as Paused there, so it must read as paused here too. A
      // connecting channel says what it's waiting on, same as the Listings page.
      statusDescription: listing.status === 'connecting' && !isDirectBooking
        ? getConnectingLabel(listing.platform)
        : describeChannelStatus(isDirectBooking ? directBookingSelectValue(listing.status) : listing.status),
      connectedAt: listing.created_at,
      // PLATFORM_MARK_LOGOS holds the marks drawn on a brand-colored circle here;
      // the rest fall back to their full-color asset rather than an empty <img>.
      logo: PLATFORM_MARK_LOGOS[listing.platform] || getListingPlatform(listing.platform)?.logo || '',
      isDirectBooking,
      lastOptimizedLabel: this.buildLastOptimizedLabel(listing),
    };
  }

  // TIDY only optimizes Airbnb listings, so no other platform (nor direct-booking
  // sites or client-only placeholder rows) gets a line — otherwise a VRBO listing
  // would read as perpetually "not optimized" for work TIDY was never going to do.
  // An Airbnb listing shows its last release date, or a "not yet" hint until its
  // first report is released.
  private buildLastOptimizedLabel(listing: AddressListing): string | null {
    if (listing.platform !== 'airbnb' || listing.isPlaceholder) {
      return null;
    }
    if (!listing.last_optimized_at) {
      return 'Not optimized yet';
    }
    return `Last optimized: ${DateTime.fromISO(listing.last_optimized_at).toFormat('MMM d, yyyy')}`;
  }

  // Every property has a direct booking site, off until the host lists it. An
  // address with no tidy listing yet still gets the row, reading as Paused; its
  // first Listed creates the listing, and the reload swaps in the real row.
  private directBookingPlaceholderRow(): ListingRow {
    return {
      id: DIRECT_BOOKING_PLACEHOLDER_ID,
      platform: DIRECT_BOOKING_PLATFORM,
      platformLabel: formatPlatformName(DIRECT_BOOKING_PLATFORM),
      platformId: '',
      rawStatus: 'unlisted',
      statusDescription: describeChannelStatus('paused'),
      connectedAt: '',
      logo: PLATFORM_MARK_LOGOS[DIRECT_BOOKING_PLATFORM],
      isDirectBooking: true,
      lastOptimizedLabel: null,
    };
  }

  // Every field loads from the real stored rows. The room list comes from the `spaces`
  // structure (photo-less rooms included) with galleries attached from `photos`; a
  // listing with neither falls back to the default empty room scaffold so there's
  // somewhere to add photos.
  private async loadEditFields(): Promise<void> {
    this.rooms = this.defaultPhotoRooms();
    try {
      const address = await this.listingSettings.getAddressWithSettings(this.addressId);
      this.rebuildSyncByKey(address.address_settings);
      const content = listingContentFromSettings(address.address_settings);
      this.loadedContent = content;
      this.amenityState = { ...content.amenities };
      this.seedStandaloneSettings(address.address_settings);
      const { rooms, hasSpacesRow } = listingRoomsFromSettings(address.address_settings);
      this.hasSpacesRow = hasSpacesRow;
      this.rooms = rooms.length ? rooms : this.defaultPhotoRooms();
      this.form.patchValue({
        title: content.title,
        description: content.description,
        securityDeposit: content.securityDepositDollars,
        bedrooms: content.bedrooms,
        bathrooms: content.bathrooms,
        maxGuests: content.maxGuests,
        squareFeet: content.squareFeet,
        placeType: content.placeType,
        neighborhoodDescription: content.neighborhoodDescription,
        cancellationPolicy: this.cancellationPolicyFrom(address.address_settings),
      }, { emitEvent: false });
      this.safetyItemState = {};
      content.safetyItems.forEach(slug => this.safetyItemState[slug] = true);
      this.houseRuleControls = content.houseRules.map(rule => this.fb.nonNullable.control(rule));
      this.highlightControls = content.highlights.map(highlight => this.newHighlightRow(highlight.title, highlight.body));
    } catch {
      // Leave the form/amenities blank and the photos on the default scaffold if load fails.
    }
    void this.seedPropertyType();
    this.rooms.forEach(room => room.photos.forEach(photo => this.registerCaptionControl(photo)));
    this.refreshRoomCounts();
  }

  // The optimizer row Booking Settings also edits; an unknown stored value reads as
  // unpicked rather than showing a tier the dropdown doesn't offer.
  private cancellationPolicyFrom(settings: AddressSetting[] | undefined): string {
    const row = (settings || []).find(setting => setting.key === 'cancellation_policy');
    const value = row?.data?.['value'];
    return this.cancellationPolicyItems.some(item => item.value === value) ? value as string : '';
  }

  // property_type lives on the address record itself, not a listing row.
  private async seedPropertyType(): Promise<void> {
    const address = await this.fetchAddress();
    const value = typeof address?.property_type === 'string' ? address.property_type : '';
    this.initialPropertyType = this.propertyTypeItems.some(item => item.value === value) ? value : '';
    this.form.patchValue({ propertyType: this.initialPropertyType }, { emitEvent: false });
    this.cdr.markForCheck();
  }

  // Pets reads from its own row, not the amenities blob, so the checkbox reflects whatever
  // Booking Settings last saved rather than showing blank next to a value that is really set.
  private seedStandaloneSettings(settings: AddressSetting[] | undefined): void {
    this.amenities.filter(amenity => amenity.settingKey).forEach(amenity => {
      const row = (settings || []).find(setting => setting.key === amenity.settingKey);
      this.amenityState[amenity.slug] = row?.data?.['value'] === true;
    });
  }

  private defaultPhotoRooms(): ListingPhotoRoom[] {
    return DEFAULT_LISTING_PHOTO_ROOMS.map(room => ({ ...room, photos: [] }));
  }

  // Re-read the listing settings after a save so the dots flip to pending on what was just
  // pushed. Refreshes only the dot data, not the form.
  private async refreshSyncStatus(): Promise<void> {
    try {
      const address = await this.listingSettings.getAddressWithSettings(this.addressId);
      this.rebuildSyncByKey(address.address_settings);
    } catch {
      // Keep the last-known dots if the refresh fails.
    }
  }

  private rebuildSyncByKey(rows: AddressSetting[] | undefined): void {
    this.syncByKey = {};
    this.syncErrorsByKey = {};
    for (const row of rows ?? []) {
      if (row.category === LISTING_SETTING_CATEGORY) {
        this.syncByKey[row.key] = row.platform_sync ?? {};
        this.syncErrorsByKey[row.key] = row.platform_sync_errors ?? {};
      }
    }
    this.rebuildPhotoRefusals();
  }

  // The gallery tile says which channel would not take that photo and why, so the customer can
  // fix the one file instead of reading "it would not take 4" and guessing which four.
  private rebuildPhotoRefusals(): void {
    const byPhoto = refusedPhotoReasons([this.syncErrorsByKey[PHOTOS_SETTING_KEY]]);
    this.photoRefusals = {};
    for (const [photoId, notes] of Object.entries(byPhoto)) {
      this.photoRefusals[photoId] = notes
        .map(note => `${formatPlatformName(note.platform)}: ${note.reason}`)
        .join(' · ');
    }
  }

  // The per-field sync dot: aggregate status over the field's platforms, plus the failed
  // platforms for the tooltip. Fields that reach no channel (bedrooms/bathrooms/sq ft)
  // return null and render nothing.
  syncDot(...keys: string[]): SyncDotInfo {
    const maps = keys.map(key => this.syncByKey[key]);
    const reasons = syncFailureReasons(keys.map(key => this.syncErrorsByKey[key]));
    return {
      status: aggregateSyncStatus(maps),
      syncedPlatforms: syncedSyncPlatforms(maps).map(formatPlatformName),
      pendingPlatforms: pendingSyncPlatforms(maps).map(formatPlatformName),
      failedPlatforms: failedSyncPlatforms(maps).map(slug => [formatPlatformName(slug), reasons[slug]].filter(Boolean).join(' — ')),
      unsupportedPlatforms: unsupportedSyncPlatforms(maps).map(formatPlatformName),
      supportedPlatforms: coveragePlatforms(keys).map(formatPlatformName),
    };
  }

  private registerCaptionControl(photo: ListingPhoto): void {
    if (this.captionControls[photo.id]) {
      return;
    }
    this.captionControls[photo.id] = this.fb.nonNullable.control(photo.caption);
  }

  private updateHeaderSpinner(): void {
    this.rightSidePanelService.headerSpinnerVisible$.next(this.isSaving);
  }

  // gotidy wraps error bodies as { error: { message, ... } }, so the human text is
  // err.error.error.message. Never HttpErrorResponse.message — that's the raw URL string.
  private errorMessage(err: unknown, fallback: string): string {
    const httpError = err as { error?: { message?: string; error?: { message?: string } } };
    return httpError?.error?.error?.message || httpError?.error?.message || fallback;
  }

  private toNumber(value: number | string | null): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    return Number(value);
  }
}
